/**
 * TruckDropDown
 *
 * A local dropdown component for the My Truck screen.
 * Identical to LHDropDown except the modal menu panel is wrapped in a
 * TouchableOpacity that stops event propagation, so tapping the search
 * TextInput does not bubble up to the backdrop's close handler and dismiss
 * the dropdown before the user can type.
 */
import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Modal,
  FlatList,
  TextInput,
  Dimensions,
  Platform,
} from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { useDarkStyles, useDarkColor } from '../../../theme/useDarkStyles';

interface DropdownItem {
  id: string | number;
  displayText: string;
  [key: string]: any;
}

interface TruckDropDownProps {
  childList: DropdownItem[];
  dropdownHeader?: string;
  feedbackType?: 'info' | 'error' | 'success' | 'warning';
  helperText?: string;
  isClearable?: boolean;
  isRequired?: boolean;
  label?: string;
  menuPosition?: string;
  placeholder?: string;
  selection?: 'single' | 'multi';
  setSelectedValue: (value: DropdownItem | DropdownItem[] | null) => void;
  selectedValue: DropdownItem | DropdownItem[] | null;
  size?: 'small' | 'medium' | 'large';
  style?: any;
  validate?: boolean;
  testID?: string;
  readOnly?: boolean;
}

const TruckDropDown: React.FC<TruckDropDownProps> = ({
  childList = [],
  dropdownHeader = '',
  feedbackType = 'info',
  helperText = '',
  isClearable = false,
  isRequired = false,
  label = '',
  menuPosition = 'bottom',
  placeholder = 'Select...',
  selection = 'single',
  setSelectedValue = () => {},
  selectedValue = null,
  size = 'medium',
  style = {},
  validate = false,
  readOnly = false,
  testID,
}) => {
  const dc = useDarkColor();
  const styles = useDarkStyles(baseStyles);
  const { t } = useTranslation('harbor');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState<DropdownItem[]>(() => {
    if (!selectedValue) return [];
    if (Array.isArray(selectedValue)) return selectedValue;
    return [selectedValue];
  });
  const [searchText, setSearchText] = useState('');
  const dropdownRef = useRef<any>(null);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
    flipped: false,
    triggerTop: 0,
  });
  const [hoveredItemId, setHoveredItemId] = useState<string | number | null>(null);

  useEffect(() => {
    if (!selectedValue) {
      setSelectedItems([]);
      return;
    }
    if (Array.isArray(selectedValue)) {
      setSelectedItems(selectedValue);
      return;
    }
    setSelectedItems([selectedValue]);
  }, [selectedValue]);

  const toggleDropdown = () => {
    if (readOnly) return;

    if (!isOpen) {
      const APPROX_MENU_HEIGHT = 220;
      const screenH = Dimensions.get('window').height;

      if (Platform.OS !== 'web') {
        if (
          dropdownRef.current &&
          typeof dropdownRef.current.measureInWindow === 'function'
        ) {
          dropdownRef.current.measureInWindow(
            (x: number, y: number, width: number, height: number) => {
              const spaceBelow = screenH - (y + height);
              const flipped = spaceBelow < APPROX_MENU_HEIGHT + 8;
              setDropdownPosition({
                top: flipped
                  ? y - APPROX_MENU_HEIGHT
                  : y + height + (Platform.OS === 'ios' ? 8 : 0),
                left: x,
                width,
                flipped,
                triggerTop: y,
              });
              setIsOpen(true);
            }
          );
        } else {
          setIsOpen(true);
        }
      } else {
        try {
          if (
            dropdownRef.current &&
            typeof dropdownRef.current.getBoundingClientRect === 'function'
          ) {
            const rect = dropdownRef.current.getBoundingClientRect();
            const scrollY =
              typeof window !== 'undefined' ? window.scrollY || 0 : 0;
            const spaceBelow = screenH - rect.bottom;
            const flipped = spaceBelow < APPROX_MENU_HEIGHT + 8;
            setDropdownPosition({
              top: flipped
                ? rect.top + scrollY - APPROX_MENU_HEIGHT
                : rect.bottom + scrollY,
              left: rect.left,
              width: rect.width,
              flipped,
              triggerTop: rect.top + scrollY,
            });
          }
          setIsOpen(true);
        } catch (error) {
          console.warn('Error positioning dropdown:', error);
          setIsOpen(true);
        }
      }
    } else {
      setIsOpen(false);
    }
  };

  const handleSelect = (item: DropdownItem) => {
    if (selection === 'single') {
      setSelectedItems([item]);
      setSelectedValue(item);
      setIsOpen(false);
      setSearchText('');
    } else {
      const isSelected = selectedItems.some(
        (s) => String(s.id).toLowerCase() === String(item.id).toLowerCase()
      );
      const newSelection = isSelected
        ? selectedItems.filter(
            (s) => String(s.id).toLowerCase() !== String(item.id).toLowerCase()
          )
        : [...selectedItems, item];
      setSelectedItems(newSelection);
      setSelectedValue(newSelection);
    }
  };

  const clearSelection = () => {
    setSelectedItems([]);
    setSelectedValue(null);
  };

  const filteredItems = childList.filter((item) =>
    item.displayText.toLowerCase().includes(searchText.toLowerCase())
  );

  const getDisplayText = () => {
    if (selectedItems.length === 0) return placeholder;
    if (selection === 'single') return selectedItems[0].displayText;
    return selectedItems.map((item) => item.displayText).join(', ');
  };

  const renderItem = ({ item }: { item: DropdownItem }) => (
    <TouchableOpacity
      style={[
        styles.item,
        selectedItems.some((s) => s.id === item.id) && styles.selectedItem,
        Platform.OS === 'web' && hoveredItemId === item.id && styles.hoverItem,
      ]}
      onPress={() => handleSelect(item)}
      {...(Platform.OS === 'web'
        ? {
            onMouseEnter: () => setHoveredItemId(item.id),
            onMouseLeave: () => setHoveredItemId(null),
          }
        : {})}
    >
      <Text style={styles.itemText}>{item.displayText}</Text>
      {selectedItems.some((s) => s.id === item.id) && (
        <MaterialIcons name="check" size={20} color={dc("#0066CC")} />
      )}
    </TouchableOpacity>
  );

  return (
    <View style={[styles.container, style]}>
      {label ? (
        <Text style={styles.label}>
          {label}
          {isRequired && <Text style={styles.required}> *</Text>}
        </Text>
      ) : null}

      <TouchableOpacity
        ref={dropdownRef}
        style={[
          styles.dropdownTrigger,
          size === 'small' && styles.smallTrigger,
          size === 'large' && styles.largeTrigger,
          readOnly && { opacity: 0.5 },
        ]}
        onPress={toggleDropdown}
        disabled={readOnly}
        testID={testID}
      >
        <Text
          style={[
            styles.selectedText,
            selectedItems.length === 0 && styles.placeholderText,
          ]}
          numberOfLines={1}
        >
          {getDisplayText()}
        </Text>
        <MaterialIcons
          name={isOpen ? 'arrow-drop-up' : 'arrow-drop-down'}
          size={20}
          color={dc("#495057")}
        />
        {isClearable && selectedItems.length > 0 && (
          <TouchableOpacity
            style={styles.clearButton}
            onPress={(e: any) => {
              e.stopPropagation();
              clearSelection();
            }}
          >
            <MaterialIcons name="close" size={18} color={dc("#495057")} />
          </TouchableOpacity>
        )}
      </TouchableOpacity>

      {helperText ? (
        <Text
          style={[
            styles.helperText,
            feedbackType === 'error' && styles.helperTextError,
            feedbackType === 'success' && styles.helperTextSuccess,
            feedbackType === 'warning' && styles.helperTextWarning,
          ]}
        >
          {helperText}
        </Text>
      ) : null}

      <Modal
        visible={isOpen}
        transparent
        animationType="fade"
        supportedOrientations={['portrait', 'landscape']}
        onRequestClose={() => setIsOpen(false)}
      >
        {/* Backdrop — tapping outside the panel closes the dropdown */}
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => { setIsOpen(false); setSearchText(''); }}
        >
          {/* Panel — stops propagation so taps here (including the search
              input) never reach the backdrop's close handler */}
          <TouchableOpacity
            activeOpacity={1}
            onPress={(e) => e.stopPropagation()}
            style={[
              styles.dropdownMenu,
              {
                top: dropdownPosition.top,
                left: menuPosition.includes('right')
                  ? dropdownPosition.left +
                    dropdownPosition.width -
                    dropdownPosition.width
                  : dropdownPosition.left,
                width: dropdownPosition.width || 200,
              },
            ]}
          >
            {dropdownHeader ? (
              <Text style={styles.dropdownHeader}>{dropdownHeader}</Text>
            ) : null}

            <View style={styles.searchContainer}>
              <MaterialIcons
                name="search"
                size={20}
                color={dc("#495057")}
                style={styles.searchIcon}
              />
              <TextInput
                style={styles.searchInput}
                placeholder={t('common.search')}
                value={searchText}
                onChangeText={setSearchText}
                placeholderTextColor={dc("#6C757D")}
                autoCorrect={false}
                autoCapitalize="none"
              />
            </View>

            <FlatList
              data={filteredItems}
              renderItem={renderItem}
              keyExtractor={(item) => item.id.toString()}
              style={styles.list}
              keyboardShouldPersistTaps="handled"
              ListEmptyComponent={
                <Text style={styles.emptyText}>{t('common.no_results_found')}</Text>
              }
            />
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>
    </View>
  );
};

const baseStyles = StyleSheet.create({
  container: {
    marginBottom: 16,
    width: '100%',
  },
  label: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#343A40',
    marginBottom: 6,
  },
  required: {
    color: '#DC3545',
  },
  dropdownTrigger: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderWidth: 1,
    borderColor: '#DEE2E6',
    borderRadius: 4,
    backgroundColor: 'white',
    minHeight: 40,
  },
  smallTrigger: {
    minHeight: 32,
    paddingVertical: 6,
  },
  largeTrigger: {
    minHeight: 48,
    paddingVertical: 12,
  },
  selectedText: {
    flex: 1,
    fontSize: 16,
    color: '#343A40',
    marginRight: 8,
  },
  placeholderText: {
    color: '#6C757D',
  },
  clearButton: {
    marginRight: 8,
  },
  helperText: {
    fontSize: 12,
    marginTop: 4,
    color: '#0066CC',
  },
  helperTextError: { color: '#DC3545' },
  helperTextSuccess: { color: '#28A745' },
  helperTextWarning: { color: '#FFC107' },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.2)',
  },
  dropdownMenu: {
    position: 'absolute',
    backgroundColor: 'white',
    borderRadius: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 4,
    elevation: 5,
    maxHeight: Dimensions.get('window').height * 0.6,
  },
  dropdownHeader: {
    padding: 12,
    fontSize: 16,
    fontWeight: '600',
    color: '#343A40',
    borderBottomWidth: 1,
    borderBottomColor: '#E9ECEF',
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#E9ECEF',
  },
  searchIcon: {
    marginRight: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    color: '#343A40',
    paddingVertical: 4,
  },
  list: {
    paddingHorizontal: 4,
    maxHeight: 144,
  },
  item: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#F1F3F5',
  },
  selectedItem: {
    backgroundColor: '#F8F9FA',
  },
  hoverItem: {
    backgroundColor: '#E9ECEF',
  },
  itemText: {
    flex: 1,
    fontSize: 16,
    color: '#343A40',
    marginRight: 8,
  },
  emptyText: {
    padding: 16,
    textAlign: 'center',
    color: '#6C757D',
  },
});

export default TruckDropDown;
